import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, Label,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const FeatureUsagePage = () => {
  const [clockData, setClockData] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [overtimeData, setOvertimeData] = useState(null);
  const [holidayData, setHolidayData] = useState(null);
  const [adoptionData, setAdoptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clock, photo, expense, overtime, holiday, adoption] = await Promise.all([
        axios.get(`${API}/super-admin/app-analytics/clock-activity`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/photo-uploads`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/expense-submissions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/overtime-requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/holiday-requests`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/feature-adoption`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setClockData(clock.data);
      setPhotoData(photo.data);
      setExpenseData(expense.data);
      setOvertimeData(overtime.data);
      setHolidayData(holiday.data);
      setAdoptionData(adoption.data);
    } catch (err) {
      console.error('Failed to fetch feature usage data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading feature usage analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Feature Usage Metrics</h1>
          <p className="text-purple-200 text-sm">Mobile app feature adoption and usage patterns</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchAll} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Feature Usage Metrics</h1>
              <p className="text-purple-200 text-sm">Mobile app feature adoption and usage patterns</p>
            </div>
          </div>
          <button onClick={fetchAll} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Chart 1: Clock-In/Out Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Clock-In/Out Activity (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={clockData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis>
              <Label value="Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Bar dataKey="clock_ins" stackId="a" fill="#10b981" name="Clock-Ins" />
            <Bar dataKey="clock_outs" stackId="a" fill="#3b82f6" name="Clock-Outs" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Daily attendance tracking usage - green bars show clock-ins, blue bars show clock-outs.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> High activity indicates strong adoption of core attendance feature. Matching clock-in/clock-out counts show good user behavior. Discrepancies suggest users forgetting to clock out (may need auto-clock-out feature). Weekday spikes are normal. Zero activity days may indicate holidays or system issues.</p>
        </div>
      </div>

      {/* Chart 2: Photo Upload Frequency */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Photo Upload Frequency (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={photoData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis>
              <Label value="Uploads" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="uploads" stroke="#f59e0b" strokeWidth={2} name="Photo Uploads" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Number of work diary photos uploaded daily by all users.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Consistent uploads show compliance with photo requirements. Low upload rates may indicate users skipping this step (enforce requirements or simplify upload process). Correlation with clock-ins shows if users are capturing work photos. Spikes may indicate catch-up uploads or specific project activities.</p>
        </div>
      </div>

      {/* Chart 3: Expense Submissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Submission Trends (Last 12 Weeks)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={expenseData?.weekly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis>
              <Label value="Submissions" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Bar dataKey="submissions" fill="#ef4444" name="Expense Submissions" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Weekly expense submission activity showing feature adoption and usage patterns.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Consistent submissions show good expense tracking adoption. End-of-month spikes are normal (monthly expense reports). Low usage suggests feature not valuable or too complex. Growing trend indicates increasing feature adoption. Use this to optimize expense approval workflows and identify busy periods.</p>
        </div>
      </div>

      {/* Chart 4: Overtime Requests */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Overtime Request Frequency (Last 12 Weeks)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={overtimeData?.weekly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis>
              <Label value="Requests" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Bar dataKey="requests" fill="#8b5cf6" name="Overtime Requests" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Weekly overtime request activity showing workload patterns and feature usage.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> High frequency indicates heavy workload periods or project demands. Seasonal spikes may correlate with busy seasons. Low usage with high work hours suggests users not claiming overtime (training issue or process too complex). Use this to plan staffing, approve overtime budgets, and identify workload trends.</p>
        </div>
      </div>

      {/* Chart 5: Holiday Requests */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Holiday Request Patterns (Last 12 Months)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={holidayData?.monthly_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis>
              <Label value="Requests" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Bar dataKey="requests" fill="#06b6d4" name="Holiday Requests" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Monthly holiday request patterns revealing seasonal vacation trends.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Summer and Christmas spikes are expected (vacation seasons). Plan staffing coverage for high-request months. Low requests in typically busy months may indicate approval issues or users not taking time off. Use this to forecast staffing needs, manage approval workflows, and ensure work-life balance.</p>
        </div>
      </div>

      {/* Chart 6: Feature Adoption Funnel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Adoption Funnel</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={adoptionData?.funnel_data || []} layout="vertical" margin={{ left: 10, right: 20, top: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number">
              <Label value="Adoption Rate (%)" position="insideBottom" offset={-10} />
            </XAxis>
            <YAxis dataKey="feature" type="category" width={130} style={{ fontSize: '12px' }} />
            <Tooltip formatter={(value, name) => name === 'percentage' ? `${value}%` : value} />
            <Legend />
            <Bar dataKey="percentage" fill="#6366f1" name="Adoption Rate (%)" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> The percentage of total users who have used each mobile app feature at least once.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> High adoption (greater than 80%) shows essential, well-discovered features. Low adoption (less than 30%) suggests features are hard to find, not valuable, or poorly explained. Clock-In should be near 100% (core feature). If Expenses or Overtime are low, improve in-app prompts or onboarding. Use this to prioritize feature improvements and identify underutilized capabilities.</p>
        </div>
      </div>
    </div>
  );
};

export default FeatureUsagePage;